/**
 * Unified Error Codes Module for Linkode
 * Provides centralized error handling with consistent API error responses
 *
 * @module errors/error-codes
 */
/**
 * API Error Code enumeration covering all possible API error types
 * @enum {string}
 */
export var ApiErrorCode;
(function (ApiErrorCode) {
    // Auth errors (401, 403)
    ApiErrorCode["UNAUTHORIZED"] = "UNAUTHORIZED";
    ApiErrorCode["FORBIDDEN"] = "FORBIDDEN";
    // Resource errors (404)
    ApiErrorCode["DOC_NOT_FOUND"] = "DOC_NOT_FOUND";
    ApiErrorCode["NOT_FOUND"] = "NOT_FOUND";
    // Validation errors (400)
    ApiErrorCode["VALIDATION_ERROR"] = "VALIDATION_ERROR";
    ApiErrorCode["BAD_REQUEST"] = "BAD_REQUEST";
    // Rate limiting (429)
    ApiErrorCode["RATE_LIMITED"] = "RATE_LIMITED";
    // Infrastructure errors (503)
    ApiErrorCode["UPSTASH_UNAVAILABLE"] = "UPSTASH_UNAVAILABLE";
    ApiErrorCode["EMAIL_SERVICE_UNAVAILABLE"] = "EMAIL_SERVICE_UNAVAILABLE";
    // Generic errors
    ApiErrorCode["INTERNAL_ERROR"] = "INTERNAL_ERROR";
    ApiErrorCode["VERSION_CONFLICT"] = "VERSION_CONFLICT";
    // Folder errors (SPEC-016)
    ApiErrorCode["FOLDER_NOT_FOUND"] = "FOLDER_NOT_FOUND";
    ApiErrorCode["FOLDER_ALREADY_EXISTS"] = "FOLDER_ALREADY_EXISTS";
    ApiErrorCode["FOLDER_DEPTH_EXCEEDED"] = "FOLDER_DEPTH_EXCEEDED";
    ApiErrorCode["INVALID_FOLDER_NAME"] = "INVALID_FOLDER_NAME";
    // Document errors (SPEC-016)
    ApiErrorCode["DOC_ALREADY_DELETED"] = "DOC_ALREADY_DELETED";
    // AI Apply errors (SPEC-014)
    ApiErrorCode["AI_APPLY_VALIDATION_ERROR"] = "AI_APPLY_VALIDATION_ERROR";
    ApiErrorCode["AI_APPLY_FRONTMATTER_PARSE_ERROR"] = "AI_APPLY_FRONTMATTER_PARSE_ERROR";
    ApiErrorCode["AI_APPLY_FAILED"] = "AI_APPLY_FAILED";
})(ApiErrorCode || (ApiErrorCode = {}));
/**
 * Mapping of ApiErrorCode to corresponding HTTP status codes
 */
export const httpStatusMap = {
    [ApiErrorCode.UNAUTHORIZED]: 401,
    [ApiErrorCode.FORBIDDEN]: 403,
    [ApiErrorCode.DOC_NOT_FOUND]: 404,
    [ApiErrorCode.NOT_FOUND]: 404,
    [ApiErrorCode.VALIDATION_ERROR]: 400,
    [ApiErrorCode.BAD_REQUEST]: 400,
    [ApiErrorCode.RATE_LIMITED]: 429,
    [ApiErrorCode.UPSTASH_UNAVAILABLE]: 503,
    [ApiErrorCode.EMAIL_SERVICE_UNAVAILABLE]: 503,
    [ApiErrorCode.INTERNAL_ERROR]: 500,
    [ApiErrorCode.VERSION_CONFLICT]: 409,
    // Folder errors (SPEC-016)
    [ApiErrorCode.FOLDER_NOT_FOUND]: 404,
    [ApiErrorCode.FOLDER_ALREADY_EXISTS]: 409,
    [ApiErrorCode.FOLDER_DEPTH_EXCEEDED]: 400,
    [ApiErrorCode.INVALID_FOLDER_NAME]: 400,
    // Document errors (SPEC-016)
    [ApiErrorCode.DOC_ALREADY_DELETED]: 410,
    // AI Apply errors (SPEC-014)
    [ApiErrorCode.AI_APPLY_VALIDATION_ERROR]: 400,
    [ApiErrorCode.AI_APPLY_FRONTMATTER_PARSE_ERROR]: 400,
    [ApiErrorCode.AI_APPLY_FAILED]: 500,
};
/**
 * Factory function for creating consistent ApiError objects
 *
 * @param code - Error code from ApiErrorCode enum
 * @param message - Human-readable error message
 * @param requestId - Request tracking ID
 * @param details - Optional additional error details
 * @returns ApiError object conforming to the interface
 */
export function createApiError(code, message, requestId, details) {
    return { code, message, requestId, details };
}
/**
 * Get HTTP status code for a given ApiErrorCode
 *
 * @param code - Error code from ApiErrorCode enum
 * @returns HTTP status code (defaults to 500 if not found)
 */
export function getHttpStatus(code) {
    return httpStatusMap[code] ?? 500;
}
//# sourceMappingURL=error-codes.js.map